'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function ContactPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const c = t.contact;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Consulta de ${formData.name} - ${formData.service || 'General'}`);
    const body = encodeURIComponent(
      `Nombre: ${formData.name}\nEmpresa: ${formData.company}\nTeléfono: ${formData.phone}\nServicio de interés: ${formData.service}\n\nMensaje:\n${formData.message}`
    );
    window.open(`mailto:info@innobox.cl?subject=${subject}&body=${body}`, '_blank');
    setSubmitted(true);
  };

  const contactMethods = [
    { icon: 'fas fa-envelope', label: c.emailLabel, value: 'info@innobox.cl', href: 'mailto:info@innobox.cl' },
    { icon: 'fab fa-whatsapp', label: c.whatsappLabel, value: '+56 9 9100 1317', href: 'https://wa.me/56991001317' },
    { icon: 'fas fa-map-marker-alt', label: c.locationLabel, value: 'Santiago, Chile', href: 'https://maps.google.com/?q=Santiago,Chile' },
    { icon: 'fas fa-clock', label: c.scheduleLabel, value: c.scheduleValue, href: null },
  ];

  const servicesEs = ['Soporte Mesa de Ayuda', 'Infraestructura y Redes', 'Ciberseguridad', 'Desarrollo de Software', 'Capacitaciones TI', 'Equipamiento Tecnológico', 'Servicios de Impresión', 'Reciclaje Electrónico', 'Asesoría y Consultoría TI', 'Otro'];
  const servicesFr = ['Support Helpdesk', 'Infrastructure et Réseaux', 'Cybersécurité', 'Développement Logiciel', 'Formations IT', 'Équipement Technologique', 'Services d\'Impression', 'Recyclage Électronique', 'Conseil et Consulting IT', 'Autre'];
  const services = language === 'fr' ? servicesFr : servicesEs;

  const companyDetails = [
    { icon: 'fas fa-building', title: c.companyRazonSocial, detail: 'INNOBOX SPA' },
    { icon: 'fas fa-map-marker-alt', title: c.companyDireccion, detail: 'Santiago, Región Metropolitana, Chile' },
    { icon: 'fas fa-envelope', title: c.companyCorreo, detail: 'info@innobox.cl' },
    { icon: 'fab fa-whatsapp', title: c.companyWhatsapp, detail: '+56 9 9100 1317' },
    { icon: 'fas fa-clock', title: c.companyHorario, detail: c.companyHorarioValue },
  ];

  const stats = [
    { value: '<24h', label: c.stat1Label },
    { value: '+10', label: c.stat2Label },
    { value: '+200', label: c.stat3Label },
    { value: '100%', label: c.stat4Label },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block">
          <img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" />
        </div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block">
          <img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" />
        </div>

        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{c.breadcrumbHome}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{c.breadcrumbContact}</span>
          </nav>

          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div
                className="inline-flex items-center gap-2 mb-6 px-4 py-2"
                style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fas fa-paper-plane" />
                <span>{c.badge}</span>
              </div>
              <h1
                className="text-white font-bold mb-6"
                style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {c.heroTitle1}<br />
                <span style={{ color: '#1AEF0F' }}>{c.heroTitle2}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {c.heroSubtitle}
              </p>
            </div>

            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '28px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section style={{ paddingTop: '70px', paddingBottom: '0', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
            {contactMethods.map((method, i) => (
              <div
                key={i}
                className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]"
                style={{
                  borderBottom: '1px solid #E2E2E2',
                  borderRight: i < contactMethods.length - 1 ? '1px solid #E2E2E2' : 'none',
                  borderTop: '1px solid #E2E2E2',
                  borderLeft: i === 0 ? '1px solid #E2E2E2' : 'none',
                }}>
                <div
                  className="mb-4 flex items-center justify-center"
                  style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${method.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-2" style={{ fontSize: '14px', letterSpacing: '1px', fontFamily: "'Nunito Sans', sans-serif" }}>
                  {method.label}
                </h3>
                {method.href ? (
                  <a
                    href={method.href}
                    target={method.href.startsWith('http') ? '_blank' : undefined}
                    rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="hover:text-[#1AEF0F] transition-colors font-semibold"
                    style={{ fontSize: '15px', color: '#111' }}>
                    {method.value}
                  </a>
                ) : (
                  <span style={{ fontSize: '15px', color: '#111', fontWeight: '600' }}>{method.value}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section style={{ paddingTop: '70px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Inquiry Form */}
            <div>
              <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>
                {c.formTitle}
              </h2>
              <p style={{ fontSize: '15px', color: '#505050', lineHeight: '1.6', marginBottom: '32px' }}>
                {c.formSubtitle}
              </p>

              {submitted ? (
                <div className="flex flex-col items-start gap-4 p-8" style={{ border: '1px solid #1AEF0F', backgroundColor: '#f0fff0' }}>
                  <i className="fas fa-check-circle text-3xl" style={{ color: '#1AEF0F' }} />
                  <h3 className="font-bold text-black" style={{ fontSize: '20px', fontFamily: "'Titillium Web', sans-serif" }}>
                    {c.successTitle}
                  </h3>
                  <p style={{ fontSize: '15px', color: '#505050', lineHeight: '1.6' }}>
                    {c.successText}{' '}
                    <a href="mailto:info@innobox.cl" className="font-semibold" style={{ color: '#1AEF0F' }}>
                      info@innobox.cl
                    </a>.
                  </p>
                  <button onClick={() => setSubmitted(false)} className="text-sm font-semibold underline" style={{ color: '#111' }}>
                    {c.successRetry}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="name" className="font-semibold" style={{ fontSize: '13px', letterSpacing: '1px', color: '#111' }}>
                        {c.fieldName}
                      </label>
                      <input id="name" name="name" type="text" required value={formData.name} onChange={handleChange}
                        placeholder="Juan Pérez"
                        className="w-full outline-none transition-all duration-200 focus:border-[#1AEF0F]"
                        style={{ border: '1px solid #E2E2E2', padding: '12px 16px', fontSize: '15px', color: '#111', backgroundColor: '#fff' }} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="email" className="font-semibold" style={{ fontSize: '13px', letterSpacing: '1px', color: '#111' }}>
                        {c.fieldEmail}
                      </label>
                      <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange}
                        placeholder="juan@empresa.cl"
                        className="w-full outline-none transition-all duration-200 focus:border-[#1AEF0F]"
                        style={{ border: '1px solid #E2E2E2', padding: '12px 16px', fontSize: '15px', color: '#111', backgroundColor: '#fff' }} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="phone" className="font-semibold" style={{ fontSize: '13px', letterSpacing: '1px', color: '#111' }}>
                        {c.fieldPhone}
                      </label>
                      <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange}
                        placeholder="+56 9 XXXX XXXX"
                        className="w-full outline-none transition-all duration-200 focus:border-[#1AEF0F]"
                        style={{ border: '1px solid #E2E2E2', padding: '12px 16px', fontSize: '15px', color: '#111', backgroundColor: '#fff' }} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="company" className="font-semibold" style={{ fontSize: '13px', letterSpacing: '1px', color: '#111' }}>
                        {c.fieldCompany}
                      </label>
                      <input id="company" name="company" type="text" value={formData.company} onChange={handleChange}
                        placeholder="Mi Empresa S.A."
                        className="w-full outline-none transition-all duration-200 focus:border-[#1AEF0F]"
                        style={{ border: '1px solid #E2E2E2', padding: '12px 16px', fontSize: '15px', color: '#111', backgroundColor: '#fff' }} />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="service" className="font-semibold" style={{ fontSize: '13px', letterSpacing: '1px', color: '#111' }}>
                      {c.fieldService}
                    </label>
                    <select id="service" name="service" value={formData.service} onChange={handleChange}
                      className="w-full outline-none transition-all duration-200 focus:border-[#1AEF0F] appearance-none"
                      style={{ border: '1px solid #E2E2E2', padding: '12px 16px', fontSize: '15px', color: formData.service ? '#111' : '#888', backgroundColor: '#fff' }}>
                      <option value="">{c.fieldServicePlaceholder}</option>
                      {services.map((s, i) => (
                        <option key={i} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="message" className="font-semibold" style={{ fontSize: '13px', letterSpacing: '1px', color: '#111' }}>
                      {c.fieldMessage}
                    </label>
                    <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange}
                      placeholder={c.fieldMessagePlaceholder}
                      className="w-full outline-none transition-all duration-200 focus:border-[#1AEF0F] resize-none"
                      style={{ border: '1px solid #E2E2E2', padding: '12px 16px', fontSize: '15px', color: '#111', backgroundColor: '#fff' }} />
                  </div>

                  <button type="submit"
                    className="group relative inline-flex items-center overflow-hidden self-start"
                    style={{ border: '1px solid #000', padding: '14px 40px', fontSize: '15px', fontWeight: 'bold', color: '#000', backgroundColor: 'transparent', transition: 'all 300ms ease-out', cursor: 'pointer' }}>
                    <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{c.submitBtn}</span>
                    <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
                  </button>
                </form>
              )}
            </div>

            {/* Company Info + Map */}
            <div className="flex flex-col gap-8">
              <div>
                <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>
                  {c.companyInfoTitle}
                </h2>
                <p style={{ fontSize: '15px', color: '#505050', lineHeight: '1.6', marginBottom: '28px' }}>
                  {c.companyInfoSubtitle}
                </p>

                <div className="flex flex-col gap-5">
                  {companyDetails.map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div
                        className="flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ width: '40px', height: '40px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                        <i className={`${item.icon} text-sm`} style={{ color: '#1AEF0F' }} />
                      </div>
                      <div>
                        <p className="font-semibold text-black m-0" style={{ fontSize: '13px', letterSpacing: '1px' }}>
                          {item.title}
                        </p>
                        <p className="m-0" style={{ fontSize: '15px', color: '#505050' }}>{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ border: '1px solid #E2E2E2', overflow: 'hidden' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d106507.48083133!2d-70.7345!3d-33.4489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662c5410425af2f%3A0x3a9c3b2b6a0b0b0b!2sSantiago%2C+Regi%C3%B3n+Metropolitana!5e0!3m2!1ses!2scl!4v1700000000000"
                  width="100%"
                  height="280"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Innobox - Santiago, Chile" />
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="https://wa.me/56991001317" target="_blank" rel="noopener noreferrer"
                  className="group relative inline-flex items-center gap-2 overflow-hidden"
                  style={{ border: '1px solid #25D366', padding: '12px 24px', fontSize: '14px', fontWeight: 'bold', color: '#25D366', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <i className="fab fa-whatsapp" />
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-white">WhatsApp</span>
                  <span className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ backgroundColor: '#25D366', zIndex: 0 }} />
                </a>
                <a href="https://calendar.app.google/qrMMuz1WHJJ4eoab8" target="_blank" rel="noopener noreferrer"
                  className="group relative inline-flex items-center gap-2 overflow-hidden"
                  style={{ border: '1px solid #000', padding: '12px 24px', fontSize: '14px', fontWeight: 'bold', color: '#000', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <i className="fas fa-calendar-alt" />
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{c.btnSchedule}</span>
                  <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
